import { useState, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import type { Photo, ValidateCodeResponse, UploadResponse } from '../types';

type UploadStep = 'code' | 'form' | 'uploading' | 'success';

interface FileWithPreview {
  file: File;
  preview: string | null;
  id: string;
}

interface UploadStatus {
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
}

export default function UploadModal() {
  const { 
    setIsUploadModalOpen, 
    userRole, 
    setUserRole,
    accessCode,
    setAccessCode,
    addPhoto 
  } = useApp();
  
  const [step, setStep] = useState<UploadStep>(userRole ? 'form' : 'code');
  const [code, setCode] = useState(accessCode);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [error, setError] = useState('');
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('/api/validate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      const data: ValidateCodeResponse = await response.json();

      if (data.valid && data.type) {
        setAccessCode(code.trim());
        setUserRole(data.type);
        setStep('form');
      } else {
        setError('Invalid code. Please try again.');
      }
    } catch {
      setError('Unable to verify code. Please try again.');
    }
  };

  const generatePreview = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      // Check if it's an image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      } else {
        // For videos, just return null - we'll show a video icon instead
        // Video thumbnail generation is unreliable on mobile
        resolve(null);
      }
    });
  };

  const isVideoFile = (file: File): boolean => {
    // Check MIME type first
    if (file.type.startsWith('video/')) return true;
    // Also check file extension for mobile compatibility
    const ext = file.name.toLowerCase().split('.').pop();
    return ['mp4', 'mov', 'avi', 'webm', 'm4v', '3gp'].includes(ext || '');
  };

  const isImageFile = (file: File): boolean => {
    if (file.type.startsWith('image/')) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext || '');
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles: FileWithPreview[] = [];
    const errors: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      // Check if file is a valid image or video
      const isImage = isImageFile(file);
      const isVideo = isVideoFile(file);
      
      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }

      const maxSize = isVideo ? 200 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        errors.push(`${file.name}: File too large (max ${maxMB}MB)`);
        continue;
      }

      const preview = await generatePreview(file);
      newFiles.push({
        file,
        preview,
        id: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
      });
    }

    if (errors.length > 0) {
      setError(errors.slice(0, 3).join('\n') + (errors.length > 3 ? `\n...and ${errors.length - 3} more` : ''));
    } else {
      setError('');
    }

    setFiles(prev => [...prev, ...newFiles]);
    
    // Reset input so same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const uploadSingleFile = async (fileItem: FileWithPreview, isFirst: boolean): Promise<boolean> => {
    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('file', fileItem.file);
    formData.append('code', accessCode);
    // Only add message to the first file
    if (isFirst && message.trim()) {
      formData.append('message', message.trim());
    }

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      const data: UploadResponse = await response.json();

      if (data.success && data.id) {
        const newPhoto: Photo = {
          id: data.id,
          uploaderName: name.trim(),
          message: isFirst ? (message.trim() || null) : null,
          fileUrl: data.url || '',
          fileType: isVideoFile(fileItem.file) ? 'video' : 'image',
          thumbnailUrl: data.url || null,
          mediumUrl: data.url || null,
          uploadedAt: new Date().toISOString(),
          fileSize: fileItem.file.size,
          mimeType: fileItem.file.type,
        };
        addPhoto(newPhoto);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0 || !name.trim()) return;

    setStep('uploading');
    setOverallProgress(0);
    setUploadedCount(0);
    setError('');

    // Initialize upload statuses
    setUploadStatuses(files.map(f => ({
      id: f.id,
      status: 'pending',
      progress: 0,
    })));

    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      
      // Update status to uploading
      setUploadStatuses(prev => prev.map(s => 
        s.id === fileItem.id ? { ...s, status: 'uploading', progress: 50 } : s
      ));

      const success = await uploadSingleFile(fileItem, i === 0);

      // Update status based on result
      setUploadStatuses(prev => prev.map(s => 
        s.id === fileItem.id ? { ...s, status: success ? 'success' : 'error', progress: 100 } : s
      ));

      if (success) {
        successCount++;
        setUploadedCount(successCount);
      }

      setOverallProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setStep('success');
  };

  const handleClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadAnother = () => {
    setFiles([]);
    setUploadStatuses([]);
    setMessage('');
    setOverallProgress(0);
    setUploadedCount(0);
    setStep('form');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop">
      <div className="absolute inset-0" onClick={handleClose} />
      
      <div className="relative bg-autumn-cream rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-fade-in">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-autumn-light-brown hover:text-autumn-brown transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8">
          {/* Code entry step */}
          {step === 'code' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-autumn-gold/10 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-autumn-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-display font-semibold text-autumn-brown">
                  Enter Access Code
                </h2>
                <p className="text-autumn-light-brown mt-2 font-body">
                  Enter the code from your invitation
                </p>
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="input-autumn text-center text-xl tracking-widest uppercase"
                  autoFocus
                  maxLength={20}
                />

                {error && (
                  <p className="text-autumn-red text-sm text-center font-body">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={!code.trim()}
                  className="btn-autumn w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </form>
            </>
          )}

          {/* Upload form step */}
          {step === 'form' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-display font-semibold text-autumn-brown">
                  Share Your Memories
                </h2>
                <p className="text-autumn-light-brown mt-2 font-body">
                  Upload photos or videos from the day
                </p>
              </div>

              <form onSubmit={handleUpload} className="space-y-4">
                {/* File previews */}
                {files.length > 0 && (
                  <div className="border-2 border-autumn-gold bg-autumn-gold/5 rounded-xl p-4">
                    <div className="flex flex-wrap gap-2 justify-center mb-3">
                      {files.slice(0, 6).map((f) => (
                        <div key={f.id} className="relative w-16 h-16">
                          {f.preview ? (
                            <img
                              src={f.preview}
                              alt="Preview"
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-full bg-autumn-gold/20 rounded-lg flex items-center justify-center">
                              <svg className="w-6 h-6 text-autumn-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {isVideoFile(f.file) && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-autumn-brown ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeFile(f.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-autumn-red text-white rounded-full flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {files.length > 6 && (
                        <div className="w-16 h-16 bg-autumn-gold/20 rounded-lg flex items-center justify-center">
                          <span className="font-body text-autumn-brown text-sm">+{files.length - 6}</span>
                        </div>
                      )}
                    </div>
                    <p className="font-body text-sm text-autumn-brown text-center">
                      {files.length} file{files.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}

                {/* File upload button - using label for mobile support */}
                <label
                  htmlFor="file-upload-input"
                  className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    files.length > 0
                      ? 'border-autumn-gold/50 hover:border-autumn-gold' 
                      : 'border-autumn-gold/30 hover:border-autumn-gold hover:bg-autumn-gold/5'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    id="file-upload-input"
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    multiple
                    className="sr-only"
                  />
                  <svg className="w-10 h-10 mx-auto text-autumn-gold/50 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="font-body text-autumn-brown">
                    {files.length > 0 ? 'Add more photos' : 'Tap to select photos or videos'}
                  </p>
                  {files.length === 0 && (
                    <p className="font-body text-sm text-autumn-light-brown mt-1">
                      Select multiple files at once
                    </p>
                  )}
                </label>

                {/* Name input */}
                <div>
                  <label className="block font-body text-sm text-autumn-brown mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    className="input-autumn"
                    required
                  />
                </div>

                {/* Message input */}
                <div>
                  <label className="block font-body text-sm text-autumn-brown mb-1">
                    Message (optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Add a message to the happy couple..."
                    className="input-autumn resize-none"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                {error && (
                  <p className="text-autumn-red text-sm text-center font-body whitespace-pre-line">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={files.length === 0 || !name.trim()}
                  className="btn-autumn w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Upload {files.length > 1 ? `${files.length} Files` : 'Photo'}
                </button>
              </form>
            </>
          )}

          {/* Uploading step */}
          {step === 'uploading' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 relative">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#8d9fb3"
                    strokeWidth="8"
                    opacity="0.2"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#e2acb0"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${overallProgress * 2.83} 283`}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-display text-xl text-autumn-brown">
                  {overallProgress}%
                </span>
              </div>
              <h2 className="text-xl font-display text-autumn-brown mb-2">
                Uploading...
              </h2>
              <p className="font-body text-autumn-light-brown mb-4">
                {uploadedCount} of {files.length} files uploaded
              </p>
              
              {/* Individual file status */}
              <div className="max-h-32 overflow-y-auto space-y-1">
                {uploadStatuses.map((status, index) => (
                  <div key={status.id} className="flex items-center gap-2 text-sm">
                    {status.status === 'pending' && (
                      <span className="w-4 h-4 rounded-full bg-autumn-gold/30" />
                    )}
                    {status.status === 'uploading' && (
                      <span className="w-4 h-4 rounded-full bg-autumn-orange animate-pulse" />
                    )}
                    {status.status === 'success' && (
                      <svg className="w-4 h-4 text-autumn-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {status.status === 'error' && (
                      <svg className="w-4 h-4 text-autumn-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="font-body text-autumn-light-brown truncate">
                      File {index + 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success step */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 bg-autumn-forest/10 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-autumn-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-display font-semibold text-autumn-brown mb-2">
                Thank You!
              </h2>
              <p className="font-body text-autumn-light-brown mb-6">
                {uploadedCount} {uploadedCount === 1 ? 'photo has' : 'photos have'} been added to the collection
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleUploadAnother}
                  className="btn-autumn w-full"
                >
                  Upload More
                </button>
                <button
                  onClick={handleClose}
                  className="btn-outline w-full"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
